import { useState } from "react";

import { ParagonCrmProvider } from "components/integrations/managed_integrations/crmProviders";
import Button from "components/ui/button/Button";
import { SendLeadRequestRecordKindEnum } from "lib/generated/integration-api";
import { useOpportunityCrmDetails } from "lib/hooks/api/integrations/useOpportunityCrmDetails";
import useParagonUser from "lib/hooks/api/integrations/useParagonUser";
import { useSendParagonCrmRecord } from "lib/hooks/api/integrations/useSendParagonCrmRecord";
import { RECORD_LABELS } from "lib/integrations/paragonCrm/recordFields";
import { EventNames, useTracking } from "lib/tracking";
import { CrmPerson, CrmRecord, SendToParagonCrmModal } from "./SendToParagonCrmModal";

type Props = {
  /** The CRM this button pushes to. */
  provider: ParagonCrmProvider;
  record: CrmRecord;
  /** The contacts saved to this record, for a mapping whose object is a person. */
  people?: CrmPerson[];
  /** Shown when this company has not connected this CRM. */
  fallback?: JSX.Element | null;
};

/**
 * The record's action for the CRM connected through Paragon: a link to it once it has been pushed,
 * otherwise the send flow. The connection is Paragon's to report rather than the integrations
 * service's, so whether it is live comes from the SDK.
 */
export function SendToParagonCrm({
  provider,
  record,
  people,
  fallback = null,
}: Props): JSX.Element | null {
  const { user } = useParagonUser();
  const { logEvent } = useTracking();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutateAsync: sendRecord } = useSendParagonCrmRecord(
    record.guid,
    record.name,
    RECORD_LABELS[record.kind],
    provider.name,
  );

  const isConnected = !!user?.integrations?.[provider.integrationKey]?.enabled;

  // Whether this record already has one in the CRM. Only asked once the connection is known,
  // because the integrations service resolves the company's CRM before it can answer.
  const { data: crmDetails, isInitialLoading } = useOpportunityCrmDetails(record.guid, {
    enabled: isConnected,
  });

  if (!isConnected) return fallback;

  if (isInitialLoading) {
    return <Button buttonType="secondary" loading />;
  }

  if (crmDetails?.externalUrl) {
    return (
      <Button
        buttonType="secondary"
        icon="linkExternal02"
        as="a"
        target="new-tab"
        href={crmDetails.externalUrl}
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          logEvent(EventNames.viewInCRMButtonClicked, {
            "Connected provider": provider.providerType,
          });
        }}
      >
        View in CRM
      </Button>
    );
  }

  return (
    <>
      <Button
        buttonType="secondary"
        icon="plus"
        // Every arm of SendOpportunityToCRM labels its button "Send to CRM", so which one rendered
        // is otherwise indistinguishable.
        data-testid={`send-record-to-${provider.integrationKey}`}
        onClick={() => {
          logEvent(EventNames.sendToCRMModalOpened, {
            "Data types": RECORD_LABELS[record.kind],
            "Connected provider": provider.providerType,
          });
          setIsModalOpen(true);
        }}
      >
        Send to CRM
      </Button>
      <SendToParagonCrmModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        provider={provider}
        record={record}
        people={people}
        onSend={(request, person) =>
          sendRecord({
            guid: record.guid,
            fields: request.fields,
            entityName: request.logicalName,
            lookups: request.lookups,
            // This button sends leads and bids; a contact has a button and a route of its own.
            recordKind:
              record.kind === "bid"
                ? SendLeadRequestRecordKindEnum.Bid
                : SendLeadRequestRecordKindEnum.Lead,
            person,
          })
        }
      />
    </>
  );
}
